import type { Dispatch, FC, SetStateAction } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useAllJobs } from "../../../../../hooks/useJobs";
import { type AutoApproveRule, FULL_SHIFT_COVERAGE } from "../../../../../utils/models";
import type { GeneralFormData } from "../types";
import { AutoApproveRulesList } from "./AutoApproveRulesList";
import type { JobCatalogState } from "./autoApproveConsts";

type Props = { formData: GeneralFormData; setFormData: Dispatch<SetStateAction<GeneralFormData>> };

/** The department's ordered auto-approval rules, edited with the page's Save action. */
export const AutoApproveSection: FC<Props> = ({ formData, setFormData }) => {
	const { track, trackError } = useAnalytics();
	const jobsQuery = useAllJobs(formData.jobIds.length > 0);
	// DEFAULT: an absent list means this department has no auto-approval rules.
	const rules = formData.autoApproveRules ?? [];
	const retryJobs = async (): Promise<void> => {
		track("auto_approve_jobs_retry_start");
		const result = await jobsQuery.refetch();
		if (result.isError) trackError("auto_approve_jobs_retry_error", { error_message: result.error.message });
		else if (result.isSuccess) track("auto_approve_jobs_retry_success");
	};
	let jobCatalog: JobCatalogState;
	if (formData.jobIds.length === 0) jobCatalog = { status: "none" };
	else if (jobsQuery.isError) jobCatalog = { status: "error", retry: () => void retryJobs() };
	else if (jobsQuery.data === undefined) jobCatalog = { status: "loading" };
	else {
		const selected = new Set(formData.jobIds);
		jobCatalog = { status: "ready", jobs: jobsQuery.data.filter((job) => selected.has(job.id)) };
	}

	const setRules = (change: (current: AutoApproveRule[]) => AutoApproveRule[]) =>
		// DEFAULT: see `rules` above.
		setFormData((prev) => ({ ...prev, autoApproveRules: change(prev.autoApproveRules ?? []) }));

	const addEmptyRule = (): string => {
		const id = uuidv4();
		setRules((current) => [
			...current,
			{
				id,
				name: "",
				jobIds: [],
				maxOt: null,
				timeBeforeShift: null,
				staffingShortage: null,
				shiftDays: [],
				shiftHours: [],
				// DEFAULT: a new rule approves whole-shift pickups only.
				minShiftCoverage: FULL_SHIFT_COVERAGE,
				// DEFAULT: a newly created department has no id until its first save.
				departmentId: formData.id ?? "",
			},
		]);
		return id;
	};

	return (
		<AutoApproveRulesList
			rules={rules}
			jobCatalog={jobCatalog}
			onAddRule={addEmptyRule}
			onChange={(index, patch) => setRules((current) => current.map((rule, other) => (other === index ? { ...rule, ...patch } : rule)))}
			onDelete={(index) => setRules((current) => current.filter((_, other) => other !== index))}
		/>
	);
};
